import type { StringIndexedObject } from '@kanda-libs/ks-component-ts';
import type { Credit, FinanceRate } from '@kanda-libs/ks-frontend-services';

import { APP_ENV } from 'config';
import { pipe } from 'fp-ts/lib/function';
import { cleanObject } from 'utils';

import {
  ALLIUM_TEST_MIDDLE_NAME,
  ETIKA_TEST_FIRST_NAME,
  OMNI_TEST_EMAIL,
} from './constants';

export const formatError = (error: StringIndexedObject): string => {
  const keys = Object.keys(error);
  if (keys.length === 0)
    return 'There is an error with the form - please scroll up to find the error field';
  const formatted = keys.map((key: string) => {
    const parts = key.split('.');
    return parts.map((part: string) => part.replace(/_/g, ' ')).join(' - ');
  });
  return `There are errors with the form in the following fields: ${formatted.join(
    ', ',
  )}`;
};

const parseLine1 = (buildingName, buildingNumber, line1) => {
  // Check if building name exists and isn't just whitespace
  const name = buildingName && /^\s*$/.test(buildingName) ? null : buildingName;
  // Check if building number exists and isn't just whitespace
  const number =
    buildingNumber && /^\s*$/.test(buildingNumber) ? null : buildingNumber;
  // Check if line 1 already includes either name or number
  if (line1.includes(name) || line1.includes(number)) return line1;
  // If building number is present, attach to line 1
  if (number) return `${number} ${line1}`;
  // If building name is present (in the absence of number), attach to line 1
  if (name) return `${name} ${line1}`;
  // If neither, return line 1 (fallback)
  return line1;
};

export const formatAddressData = (address) => {
  const {
    selected,
    building_name: buildingName,
    building_number: buildingNumber,
    postcode,
    line_1: line1,
    ...rest
  } = address;

  const parsedLine1 = parseLine1(
    buildingName || null,
    buildingNumber || null,
    line1,
  );

  return {
    ...rest,
    ...(buildingName && { building_name: buildingName }),
    ...(buildingNumber && { building_number: buildingNumber }),
    line_1: parsedLine1,
    postcode: postcode.toUpperCase(),
  };
};

const formatAllAddressesData = (current, prev1, prev2) => {
  const includePrev1 = (current?.months_at_address || 0) < 36;
  const includePrev2 =
    (current?.months_at_address || 0) + (prev1?.months_at_address || 0) < 36;

  return {
    current_address: formatAddressData(current),
    goods_delivery_address: formatAddressData(current),
    ...(includePrev1 && { previous_address_one: formatAddressData(prev1) }),
    ...(includePrev2 && { previous_address_two: formatAddressData(prev2) }),
  };
};

const formatCustomerDetails = (details, shortForm = false, credit: Credit) => {
  if (shortForm) {
    const rent = (credit?.finance_details?.expenditures || [])?.find(
      (expense) => expense.expenditure_type === 'rent',
    );

    if (!rent) return details;

    return {
      ...details,
      monthly_rent_amount: {
        ...(details.monthly_rent_amount || {}),
        ...rent.monthly_amount,
      },
    };
  }
  const {
    current_address: currentAddress,
    previous_address_one: previousAddressOne,
    previous_address_two: previousAddressTwo,
    date_of_birth: dateOfBirth,
    ...rest
  } = details;
  const addresses = formatAllAddressesData(
    currentAddress,
    previousAddressOne,
    previousAddressTwo,
  );
  const [day, month, year] = dateOfBirth.split('/');
  return cleanObject({
    ...rest,
    date_of_birth: new Date([year, month, day].join('-')).toISOString(),
    ...addresses,
  });
};

const formatSpecificEmploymentDetails = (
  details: StringIndexedObject,
  status: string,
  shortForm = false,
) => {
  if (status === 'employed') {
    const {
      employer_address: employerAddress,
      placeholder,
      ...rest
    } = details?.employed_details || {};
    return {
      employed_details: {
        ...rest,
        ...(!shortForm && {
          employer_address: formatAddressData(employerAddress),
        }),
      },
    };
  }
  if (status === 'self_employed') {
    const {
      business_address: businessAddress,
      placeholder,
      ...rest
    } = details?.self_employed_details || {};
    return {
      self_employed_details: {
        ...rest,
        ...(!shortForm && {
          business_address: formatAddressData(businessAddress),
        }),
      },
    };
  }
  if (status === 'not_employed') {
    const { placeholder, ...rest } = details?.not_employed_details || {};
    return {
      not_employed_details: {
        ...rest,
      },
    };
  }
  if (status === 'retired') {
    const { placeholder, ...rest } = details?.retired_details || {};
    return {
      retired_details: {
        ...rest,
      },
    };
  }

  return details;
};

const formatEmploymentDetails = (details, shortForm = false) => {
  const {
    employment_status: employmentStatus,
    gross_annual_income: grossAnnualIncome,
    household_annual_income: householdAnnualIncome,
    ...rest
  } = details;

  const employmentDetails = formatSpecificEmploymentDetails(
    rest,
    employmentStatus,
    shortForm,
  );
  return cleanObject({
    employment_status: employmentStatus,
    gross_annual_income: grossAnnualIncome,
    household_annual_income: householdAnnualIncome,
    ...employmentDetails,
  });
};

const formatAdditionalEmploymentDetails = (
  additionalEmploymentDetails,
  employmentDetails,
) => {
  if (!additionalEmploymentDetails || additionalEmploymentDetails.length === 0)
    return null;
  return additionalEmploymentDetails.map((employment) =>
    formatEmploymentDetails({
      ...employmentDetails,
      ...employment,
    }),
  );
};

const addEtikaTestData = (credit: Credit, status: string) => {
  const firstName = ETIKA_TEST_FIRST_NAME?.[status];
  if (!firstName) return credit;
  return {
    ...credit,
    customer_details: {
      ...credit.customer_details,
      first_name: firstName,
    },
  };
};

const addAlliumTestData = (credit: Credit, status: string) => {
  const middleName = ALLIUM_TEST_MIDDLE_NAME?.[status];
  if (!middleName) return credit;
  return {
    ...credit,
    customer_details: {
      ...credit.customer_details,
      middle_name: middleName,
    },
  };
};

const addOmniTestData = (credit: Credit, status: string) => {
  const email = OMNI_TEST_EMAIL?.[status];
  if (!email) return credit;
  return {
    ...credit,
    customer_details: {
      ...credit.customer_details,
      email,
    },
  };
};

const testFirstName = (name?: string): string | undefined => {
  if (!name) return undefined;
  if (name.toLowerCase().includes('accept')) return 'ACCEPT';
  if (name.toLowerCase().includes('decline')) return 'DECLINE';
  if (name.toLowerCase().includes('refer')) return 'REFER';
  if (name.toLowerCase().includes('mixed')) return 'MIXED';
  return undefined;
};

export const replaceTestData = (credit: Credit) => {
  if (APP_ENV !== 'qa') return credit;
  const provider = credit?.finance_option?.provider;
  const status = testFirstName(credit?.customer_details?.first_name);
  if (!provider || !status) return credit;
  if (provider === 'omni') return addOmniTestData(credit, status);
  if (provider === 'allium') return addAlliumTestData(credit, status);
  if (provider === 'etika') return addEtikaTestData(credit, status);
  return credit;
};

interface RawDataInput {
  data: StringIndexedObject;
  financeRate: FinanceRate;
  shortForm?: boolean;
}

export const formatRawData = ({
  data,
  financeRate,
  shortForm,
}: RawDataInput): Credit => {
  const { credit } = data;
  return {
    finance_option: financeRate,
    bank_account: credit?.bank_account || null,
    finance_details: {
      ...(credit?.finance_details || {}),
      bankrupcy_or_iva_in_last_5_years: 'no',
    },
    customer_details: formatCustomerDetails(
      credit?.customer_details,
      shortForm,
      credit,
    ),
    employment_details: formatEmploymentDetails(
      credit?.employment_details,
      shortForm,
    ),
    additional_employment_details: formatAdditionalEmploymentDetails(
      credit?.additional_employment_details,
      credit?.employment_details,
    ),
  };
};

export const formatDataForApi = (
  data: StringIndexedObject,
  financeRate: FinanceRate,
  shortForm = false,
) =>
  pipe(
    { data, financeRate, shortForm },
    formatRawData,
    replaceTestData,
    cleanObject,
  );
